import { createSignal, For, onMount, Show } from "solid-js";
import { api, type Organization, type RealtimeConnectionInfo } from "../shared";
import { ExpandablePayload, DirectionIndicator, CopyButton } from "./EventHelpers";

export function RealtimeConnectionsTable(props: {
  connections: RealtimeConnectionInfo[];
  selectedConnectionIds: Set<string>;
  onToggleConnection: (connId: string) => void;
  onOpenDetail: (conn: RealtimeConnectionInfo) => void;
  allEvents: any[];
  onRefresh: () => void;
}) {
  const [orgs, setOrgs] = createSignal<Organization[]>([]);
  const [filterOrgId, setFilterOrgId] = createSignal("");
  const [filterProjectId, setFilterProjectId] = createSignal("");
  const [expandedConnections, setExpandedConnections] = createSignal<Set<string>>(new Set());

  onMount(() => {
    api.organizations.list().then(setOrgs).catch(() => {});
  });

  const filtered = () => {
    let conns = props.connections;
    if (filterOrgId()) {
      conns = conns.filter((c) => c.organizationId === filterOrgId());
    }
    if (filterProjectId()) {
      conns = conns.filter((c) => c.projectId === filterProjectId());
    }
    return conns;
  };

  const orgName = (orgId: string | null | undefined) => {
    if (!orgId) return "—";
    const org = orgs().find((o) => o.id === orgId);
    return org?.name ?? orgId.slice(0, 10);
  };

  const eventsForConnection = (connId: string) => {
    return props.allEvents.filter((evt: any) =>
      evt.sender?.connectionId === connId
    );
  };

  function toggleExpand(connId: string) {
    setExpandedConnections((prev) => {
      const next = new Set(prev);
      if (next.has(connId)) next.delete(connId);
      else next.add(connId);
      return next;
    });
  }

  return (
    <div>
      <div class="flex items-center justify-between mb-3 gap-3">
        <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Connections ({filtered().length})</h3>
        <div class="flex items-center gap-2">
          <select value={filterOrgId()} onChange={(e) => setFilterOrgId(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 cursor-pointer">
            <option value="">All orgs</option>
            <For each={orgs()}>{(o) => <option value={o.id}>{o.name}</option>}</For>
          </select>
          <select value={filterProjectId()} onChange={(e) => setFilterProjectId(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 cursor-pointer">
            <option value="">All projects</option>
            <For each={[...new Set(props.connections.map((c) => c.projectId).filter(Boolean))]}>
              {(pid) => <option value={pid}>{pid.slice(0, 12)}</option>}
            </For>
          </select>
          <button onClick={props.onRefresh} class="px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:text-gray-200 cursor-pointer">Refresh</button>
        </div>
      </div>
      <div class="border border-gray-800 rounded-lg overflow-hidden max-h-[50vh] overflow-x-auto">
        <table class="w-full text-xs font-mono min-w-[800px]">
          <thead class="bg-gray-900 text-gray-400 sticky top-0 z-10">
            <tr>
              <th class="px-3 py-2 font-medium border-b border-gray-800 w-6"></th>
              <th class="px-3 py-2 font-medium border-b border-gray-800 w-6"></th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Connection</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Org</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">User</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Role</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Project</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Events</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Connected</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800/50">
            <For each={filtered()}>
              {(c) => {
                const isSelected = () => props.selectedConnectionIds.has(c.connectionId);
                const isExpanded = () => expandedConnections().has(c.connectionId);
                const connEvents = () => eventsForConnection(c.connectionId).slice(0, 50);
                const eventCount = () => eventsForConnection(c.connectionId).length;

                return (
                  <>
                    <tr class={`hover:bg-gray-900/30 ${isSelected() ? "bg-cyan-900/20" : ""}`}>
                      <td class="px-3 py-1.5">
                        <input type="checkbox" checked={isSelected()} onChange={() => props.onToggleConnection(c.connectionId)} class="rounded bg-gray-700 border-gray-600 cursor-pointer" />
                      </td>
                      <td class="px-1 py-1.5">
                        <button onClick={() => toggleExpand(c.connectionId)} class="text-gray-500 hover:text-gray-200 cursor-pointer w-4 h-4 flex items-center justify-center text-[10px] leading-none">
                          {isExpanded() ? "▼" : "►"}
                        </button>
                      </td>
                      <td class="px-3 py-1.5">
                        <button onClick={() => props.onOpenDetail(c)} class="text-cyan-400 hover:text-cyan-300 cursor-pointer font-mono">{c.connectionId?.slice(0, 12)}</button>
                      </td>
                      <td class="px-3 py-1.5 text-gray-400">{orgName(c.organizationId)}</td>
                      <td class="px-3 py-1.5 text-gray-300">{c.userId?.slice(0, 10)}</td>
                      <td class="px-3 py-1.5">
                        <span class={`${c.role === "searcher" ? "text-cyan-400" : c.role === "manager" ? "text-purple-400" : "text-gray-400"}`}>{c.role}</span>
                      </td>
                      <td class="px-3 py-1.5 text-gray-500">{c.projectId?.slice(0, 10)}</td>
                      <td class="px-3 py-1.5 text-gray-400">{eventCount() || "—"}</td>
                      <td class="px-3 py-1.5 text-gray-500">{new Date(c.connectedAt).toLocaleTimeString()}</td>
                    </tr>
                    <Show when={isExpanded() && connEvents().length > 0}>
                      <tr>
                        <td colspan={2}></td>
                        <td colspan={8} class="bg-gray-950/50 border-t border-gray-800/30">
                          <div class="max-h-36 overflow-y-auto overflow-x-auto">
                            <table class="w-full text-[10px] font-mono min-w-[600px]">
                              <thead>
                                <tr class="text-gray-500 border-b border-gray-800/30">
                                  <th class="text-left px-3 py-1 font-medium">Time</th>
                                  <th class="text-left px-3 py-1 font-medium">Type</th>
                                  <th class="text-left px-3 py-1 font-medium w-12">Dir</th>
                                  <th class="text-left px-3 py-1 font-medium">Payload</th>
                                  <th class="px-1 py-1 w-8"></th>
                                </tr>
                              </thead>
                              <tbody class="divide-y divide-gray-800/20">
                                <For each={connEvents()}>
                                  {(evt: any) => (
                                    <tr class="hover:bg-gray-900/20">
                                      <td class="px-3 py-0.5 text-gray-500 whitespace-nowrap">{new Date(evt.timestamp).toLocaleTimeString()}</td>
                                      <td class="px-3 py-0.5 text-cyan-400 whitespace-nowrap">{evt.original?.type ?? "?"}</td>
                                      <td class="px-3 py-0.5"><DirectionIndicator channels={evt.channels} /></td>
                                      <td class="px-3 py-0.5 min-w-[150px]"><ExpandablePayload payload={evt.original} /></td>
                                      <td class="px-1 py-0.5"><CopyButton text={JSON.stringify(evt.original, null, 2)} /></td>
                                    </tr>
                                  )}
                                </For>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </Show>
                    <Show when={isExpanded() && connEvents().length === 0}>
                      <tr>
                        <td colspan={2}></td>
                        <td colspan={8} class="bg-gray-950/50 border-t border-gray-800/30 px-3 py-2 text-[10px] text-gray-600">No messages captured yet</td>
                      </tr>
                    </Show>
                  </>
                );
              }}
            </For>
          </tbody>
        </table>
      </div>
    </div>
  );
}
